<template>
  <SidebarSettingsGroup
    title="Style & Animation"
    collapsible
    :element-type="element.type"
    data-test="graphics-triangleSettings-styleAndAnimation"
  >
    <opacity-settings v-model="opacity" />

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="sidebar-form-label">Background color</label>
      <label
        class="radio inline-radio"
        for="solid"
      >Solid
        <input
          id="solid"
          v-model="fillStyle"
          type="radio"
          value="solid"
          data-test="graphics-triangleSettings-fillStyleSolid"
          data-entity="graphics-triangleSettings-fillStyleSolid"
        />
        <div class="checkmark"></div>
      </label>
      <label
        class="radio inline-radio"
        for="gradient"
      >Gradient
        <input
          id="gradient"
          v-model="fillStyle"
          type="radio"
          value="gradient"
          data-test="graphics-triangleSettings-fillStyleGradient"
          data-entity="graphics-triangleSettings-fillStyleGradient"
        />
        <div class="checkmark"></div>
      </label>

      <div
        v-if="fillStyle == 'solid'"
        class="colorpicker text-center"
        data-test="graphics-triangleSettings-fillStyleSolidColor"
      >
        <colorpicker
          v-model="backgroundColor"
          :is-mapped="checkMapped('backgroundColor')"
        />
        <span
          data-balloon="This setting is controlled by data. Unmap data to customize."
          data-balloon-pos="down"
          data-balloon-length="medium"
        >
          <span
            v-if="checkMapped('backgroundColor')"
            class="fa fa-lock mapped-lock"
          />
        </span>
      </div>

      <div
        v-else
        class="settings-row settings-row-xs"
        data-test="graphics-triangleSettings-fillStyleGradient"
      >
        <div class="settings-col-6 settings-col-xs">
          <div class="input-group">
            <validation-field as="input"
              v-model.number="gradientDirection"
              rules="required|numeric"
              type="number"
              name="gradientDirection"
              :class="{ 'is-invalid': validationErrors['gradientDirection'] }"
              data-test="graphics-triangleSettings-setGradientDirection"
              data-entity="graphics-triangleSettings-setGradientDirection"
            />
            <span class="input-group-addon symbol">°</span>
          </div>
        </div>
        <div class="settings-col-3 settings-col-xs colorpicker text-center">
          <colorpicker
            v-model="gradientColorOne"
            :is-mapped="checkMapped('gradientColorOne')"
          />
          <span
            data-balloon="This setting is controlled by data. Unmap data to customize."
            data-balloon-pos="down"
            data-balloon-length="medium"
          >
            <span
              v-if="checkMapped('gradientColorOne')"
              class="fa fa-lock mapped-lock"
            />
          </span>
        </div>
        <div class="settings-col-3 settings-col-xs colorpicker text-center">
          <colorpicker
            v-model="gradientColorTwo"
            :is-mapped="checkMapped('gradientColorTwo')"
          />
          <span
            data-balloon="This setting is controlled by data. Unmap data to customize."
            data-balloon-pos="down-right"
            data-balloon-length="medium"
          >
            <span
              v-if="checkMapped('gradientColorTwo')"
              class="fa fa-lock mapped-lock"
            />
          </span>
        </div>
      </div>

      <!-- TODO: remove once sheets can have more than one thing mapped -->
      <div
        v-if="showDataMapControls"
        class="margin-top-sm"
        data-test="graphics-triangleSettings-dataMapControls"
      >
        <p
          v-if="isBackgroundColorMapped"
          class="sidebar-form-label margin-top-sm"
        >
          From Data Provider
        </p>

        <map-data-control
          v-if="fillStyle == 'solid'"
          :data-provider="dataProvider"
          :element="element"
          :scroll-offset="scrollOffset"
          property="backgroundColor"
          :mapped="checkMapped('backgroundColor')"
          @update-map="mapToField"
          @clear-map="clearMapping"
        />
        <map-data-control
          v-if="fillStyle == 'gradient'"
          :data-provider="dataProvider"
          :element="element"
          :scroll-offset="scrollOffset"
          property="gradientColorOne"
          :mapped="checkMapped('gradientColorOne')"
          @update-map="mapToField"
          @clear-map="clearMapping"
        />
        <map-data-control
          v-if="fillStyle == 'gradient'"
          :data-provider="dataProvider"
          :element="element"
          :scroll-offset="scrollOffset"
          property="gradientColorTwo"
          :mapped="checkMapped('gradientColorTwo')"
          @update-map="mapToField"
          @clear-map="clearMapping"
        />
      </div>
    </div>

    <hr class="sidebar-divider" />

    <background-image-settings
      :element="element"
      :scroll-offset="scrollOffset"
      @modal="(opts) => $emit('modal', opts)"
    />

    <hr class="sidebar-divider" />

    <animation-settings :element="element"></animation-settings>
  </SidebarSettingsGroup>
</template>

<script>
import SettingsMixins from '../../mixins/settings-mixins';

import BackgroundImageSettings from './BackgroundImage.vue';
import AnimationSettings from './Animation.vue';
import OpacitySettings from './Opacity.vue';
import MapDataControl from './Data/MapDataControl.vue';

export default {
  emits: ['modal'],
  name: 'TriangleSettingsComponent',
  components: {
    AnimationSettings,
    BackgroundImageSettings,
    OpacitySettings,
    MapDataControl,
  },
  mixins: [SettingsMixins],
  props: ['element', 'scrollOffset'],
  computed: {
    opacity: {
      get() {
        return this.getPropertyValue('styles.opacity', { emptyValue: 1 });
      },
      set(v) {
        this.updateOneElementValue('styles.opacity', v);
      },
    },
    fillStyle: {
      get() {
        return this.getPropertyValue('fillStyle');
      },
      set(v) {
        this.updateOneElementValue('fillStyle', v);
      },
    },
    backgroundColor: {
      get() {
        return this.getPropertyValue('backgroundColor', { mappedPath: 'backgroundColor' });
      },
      set(v) {
        this.updateOneElementValue('backgroundColor', v);
      },
    },
    gradientDirection: {
      get() {
        return this.getPropertyValue('gradientDirection');
      },
      set(v) {
        this.updateOneElementValue('gradientDirection', v);
      },
    },
    gradientColorOne: {
      get() {
        return this.getPropertyValue('gradientColorOne', { mappedPath: 'gradientColorOne' });
      },
      set(v) {
        this.updateOneElementValue('gradientColorOne', v);
      },
    },
    gradientColorTwo: {
      get() {
        return this.getPropertyValue('gradientColorTwo', { mappedPath: 'gradientColorTwo' });
      },
      set(v) {
        this.updateOneElementValue('gradientColorTwo', v);
      },
    },
  },
};
</script>
